import { Title } from "@/components/PredictionMarket/MarketCardList/Title";
import { Progress } from "@/components/ui/progress";
import { useHandleError } from "@/hooks/useHandleError";
import { ThumbaCoin } from "@roman/shared/components/Icons";
import { useGetRelatedRecentPredictionMarkets } from "@roman/shared/hooks/PredictionMarket/useGetRelatedRecentPredictionMarkets";
import type { PredictionMarketChoiceWithProbability } from "@roman/shared/types/PredictionMarket";
import {
  PredictionMarketType,
  getPredictionMarketType,
  getYesNoChoices,
} from "@roman/shared/utils/predictionMarket/utils";
import { maxBy } from "lodash";
import { useNavigate } from "react-router-dom";

const RelatedPredictionMarket = ({ label, id }: { label: string; id: string }) => {
  const navigate = useNavigate();
  const { relatedPredictionMarkets, isLoading } = useGetRelatedRecentPredictionMarkets({
    id,
    label,
    swrOptions: {
      onError: useHandleError("マイリザルトの取得に失敗しました"),
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  });

  if (isLoading || !relatedPredictionMarkets?.length) {
    return null;
  }

  return (
    <div className="flex flex-col">
      <div className="font-extrabold text-[18px] leading-[1.4] tracking-[-0.02em] text-black h-[25px]">関連クイズ</div>

      <div className="flex overflow-x-scroll hide-scrollbar gap-4 w-full mt-[14.49px]">
        {relatedPredictionMarkets.map((market) => {
          const predictionMarketType = getPredictionMarketType(market.predictionMarketChoices);
          return (
            <div
              className="flex flex-col cursor-pointer"
              key={market.id}
              onClick={() => {
                navigate(`/prediction-market/details/${market.id}`);
              }}
            >
              <div className="w-[244px] h-[173px] bg-white rounded-md overflow-hidden flex-shrink-0 pt-4 pb-5 px-3 flex items-stretch flex-col gap-2">
                <div className="flex-1">
                  <div className="flex items-center justify-center rounded-[60px] w-fit border border-dark-gray bg-white px-3">
                    <span className="text-dark-gray font-medium text-[10px]">{market.subLabel}</span>
                  </div>

                  <div className="flex items-center justify-between gap-[6px] mt-[5px]">
                    {market.primaryImageURL && (
                      <img
                        src={market.primaryImageURL}
                        alt={market.title}
                        className="object-cover rounded-lg h-[31px] w-[31px]"
                      />
                    )}
                    <Title
                      text={market.title}
                      className="text-black font-sans text-xs font-bold leading-[1.4] tracking-[-0.24px] text-ellipsis overflow-hidden"
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-2">
                  <div>
                    {predictionMarketType === PredictionMarketType.MultipleChoice ? (
                      <MultipleChoices choices={market.predictionMarketChoices} />
                    ) : (
                      <YesNoChoices choices={market.predictionMarketChoices} />
                    )}
                  </div>
                  <div className="flex items-center gap-1 pt-1">
                    <ThumbaCoin className="w-4 h-4" />
                    <span className="text-black font-bold text-sm">
                      {Number(market.totalEnteredFreePoints).toLocaleString()} pt
                    </span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const MultipleChoices = ({
  choices,
}: {
  choices: PredictionMarketChoiceWithProbability[];
}) => {
  const maxChoice = maxBy(choices, (choice) => choice.probability);

  if (!maxChoice) {
    return null;
  }

  return (
    <div className="flex items-center gap-2 bg-primary/10 px-2 py-[5px] w-full h-[40px] rounded-md justify-between">
      <div className="flex-1 flex gap-1 items-center">
        <span className="bg-primary rounded-full w-2.5 h-2.5" />
        <span className="text-xs font-medium">{maxChoice.name}</span>
      </div>
      <div className="text-xs font-extrabold">{Math.round(maxChoice.probability * 100)}%</div>
    </div>
  );
};

const YesNoChoices = ({
  choices,
}: {
  choices: PredictionMarketChoiceWithProbability[];
}) => {
  const { yesChoice, noChoice } = getYesNoChoices(choices);

  const yesPercentage =
    yesChoice.probability * 100 >= 1
      ? Math.round(yesChoice.probability * 100) === 100
        ? "99"
        : Math.round(yesChoice.probability * 100)
      : "<1";
  const noPercentage =
    noChoice.probability * 100 >= 1
      ? Math.round(noChoice.probability * 100) === 100
        ? "99"
        : Math.round(noChoice.probability * 100)
      : "<1";

  return (
    <div>
      <div className="flex justify-between items-end w-full">
        <div className="text-black font-medium text-[10px] leading-[15.952px] tracking-[-0.228px]">
          <span className="text-[#00AFCE]">{yesChoice.name}</span> <span>{yesPercentage}%</span>
        </div>
        <div className="text-black font-medium text-[10px] leading-[15.952px] tracking-[-0.228px]">
          <span className="text-[#FF7FD2]">{noChoice.name}</span> <span>{noPercentage}%</span>
        </div>
      </div>

      <Progress
        value={Number((yesChoice.probability * 100).toFixed(2))}
        className="w-full h-[6.54px] mt-[2px]"
        indicatorColor="bg-[#00AFCE]"
        trackColor="bg-[#FF7FD2]"
      />
    </div>
  );
};

export default RelatedPredictionMarket;
